import { useEffect, useMemo, useState } from "react";
import type { Kennel, Membership, MembershipPeriod } from "@hash/shared";
import { api } from "../api/client";
import { parseApiError } from "../api/errors";
import { formatMoney, todayLocalISO } from "../lib/checkInStatus";

// One purchasable period annotated with the scheme it belongs to (for the
// picker label — a kennel can be covered by several schemes, Story 10.3).
type PickPeriod = MembershipPeriod & { schemeName: string };

/**
 * Record a cash membership payment for a hasher (Story 10.1). A payment always
 * buys exactly one materialized period; when more than one is purchasable the
 * recorder picks, defaulting to the period covering today. Reused from the
 * check-in lapsed/unpaid marker (kennel fixed) and the hasher record (kennel
 * chosen from the managed kennels the recorder manages).
 */
export function RecordDuesModal({
  hasher,
  fixedKennel,
  managedKennels,
  onClose,
  onRecorded,
}: {
  hasher: { id: string; name: string };
  // When set, dues are recorded for this kennel and the picker is locked to it.
  fixedKennel?: { id: string; name: string };
  // When no fixed kennel, the recorder chooses among these (managed) kennels.
  managedKennels?: Kennel[];
  onClose: () => void;
  onRecorded: (kennelId: string, membership: Membership) => void;
}) {
  const today = todayLocalISO();
  const [kennelId, setKennelId] = useState(
    fixedKennel?.id ?? managedKennels?.[0]?.id ?? ""
  );
  // null = still loading the periods for the selected kennel.
  const [periods, setPeriods] = useState<PickPeriod[] | null>(null);
  const [selectedId, setSelectedId] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const kennelName =
    fixedKennel?.name ??
    managedKennels?.find((k) => k.id === kennelId)?.name ??
    "";

  // Load the purchasable periods whenever the target kennel changes. The GET
  // also lazily materializes any period whose window has opened (Story 10.4).
  useEffect(() => {
    if (!kennelId) return;
    let cancelled = false;
    setPeriods(null);
    setErr(null);
    api.schemes
      .list(kennelId)
      .then(({ schemes }) => {
        if (cancelled) return;
        const flat: PickPeriod[] = [];
        for (const s of schemes) {
          if (s.retired) continue; // retired schemes sell no new periods
          for (const p of s.purchasable) {
            flat.push({ ...p, schemeName: s.name });
          }
        }
        flat.sort((a, b) => a.start.localeCompare(b.start));
        setPeriods(flat);
        // Default to the period covering today, else the first upcoming one.
        const covering = flat.find((p) => p.start <= today && today <= p.end);
        setSelectedId((covering ?? flat[0])?.id ?? "");
      })
      .catch((e) => {
        if (!cancelled) setErr(parseApiError(e).message);
      });
    return () => {
      cancelled = true;
    };
  }, [kennelId, today]);

  const selected = useMemo(
    () => periods?.find((p) => p.id === selectedId),
    [periods, selectedId]
  );

  const record = async () => {
    if (!selected) return;
    setBusy(true);
    setErr(null);
    try {
      const { membership } = await api.memberships.record(
        kennelId,
        hasher.id,
        selected.id
      );
      onRecorded(kennelId, membership);
    } catch (e) {
      setErr(parseApiError(e).message);
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-slate-900/40 sm:items-center"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Record dues for ${hasher.name}`}
        className="w-full rounded-t-2xl bg-white p-5 sm:max-w-sm sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold text-slate-900">Record dues</h2>
        <p className="mt-1 text-sm text-slate-500">
          Cash membership for {hasher.name}
          {kennelName ? ` at ${kennelName}` : ""}.
        </p>

        {!fixedKennel && managedKennels && managedKennels.length > 0 && (
          <label className="mt-4 block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Kennel
            </span>
            <select
              value={kennelId}
              onChange={(e) => setKennelId(e.target.value)}
              aria-label="Kennel"
              className="h-12 w-full rounded-lg border border-slate-300 px-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
            >
              {managedKennels.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="mt-4">
          {periods === null ? (
            <p className="text-sm text-slate-500">Loading periods…</p>
          ) : periods.length === 0 ? (
            <p className="text-sm text-slate-500">
              No membership period is open for purchase
              {kennelName ? ` at ${kennelName}` : ""} right now.
            </p>
          ) : (
            <fieldset className="space-y-2">
              <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Period
              </legend>
              {periods.map((p) => (
                <label
                  key={p.id}
                  className={`flex cursor-pointer items-center justify-between gap-3 rounded-lg border px-3 py-3 ${
                    selectedId === p.id
                      ? "border-slate-900 bg-slate-50"
                      : "border-slate-200"
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block font-medium text-slate-900">
                      {p.start} – {p.end}
                    </span>
                    <span className="block text-xs text-slate-500">
                      {p.schemeName} · {formatMoney(p.price)}
                    </span>
                  </span>
                  <input
                    type="radio"
                    name="dues-period"
                    value={p.id}
                    checked={selectedId === p.id}
                    onChange={() => setSelectedId(p.id)}
                    className="h-5 w-5 shrink-0 accent-slate-900"
                  />
                </label>
              ))}
            </fieldset>
          )}
        </div>

        {err && (
          <p className="mt-3 text-sm text-red-600" role="alert">
            {err}
          </p>
        )}

        <div className="mt-4 flex gap-2">
          <button
            onClick={onClose}
            className="h-12 flex-1 rounded-lg bg-slate-100 font-medium text-slate-700"
          >
            {periods && periods.length === 0 ? "Close" : "Cancel"}
          </button>
          {periods && periods.length > 0 && (
            <button
              onClick={record}
              disabled={busy || !selected}
              className="h-12 flex-1 rounded-lg bg-slate-900 font-medium text-white disabled:opacity-50"
            >
              {busy
                ? "Recording…"
                : selected
                  ? `Record ${formatMoney(selected.price)}`
                  : "Record"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
